import React, { useState, useEffect } from 'react';
import { IconEdit, IconPlus } from '../icons';
import { comum } from '../../styles/Themes/light';
import ButtonDefault from '../form/ButtonDefault';

import {
  Container,
  SelectorButton,
  Modal,
  ModalContent,
  ModalHeader,
  CloseButton,
  SearchContainer,
  OfficeList,
  OfficeItem,
  NoResultsMessage
} from './styles';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { toastDefs, toastDefsError } from '../../config/toastConfig';

export interface Professional {
  id_usuario: number;
  nome: string;
}

interface ProfessionalSelectorProps {
  selectedProfessional: Professional | null;
  onSelectProfessional: (id_usuario?: number) => void;
  id_proposta: number;
  error?: string;
  placeholder?: string;
  className?: string;
  isAlteracao?: boolean;
  openModalExternally?: boolean;
  onModalClose?: () => void;
}

const ProfessionalSelector: React.FC<ProfessionalSelectorProps> = ({
  selectedProfessional,
  onSelectProfessional,
  id_proposta,
  error,
  placeholder = "Adicionar Profissional",
  className,
  isAlteracao = false,
  openModalExternally = false,
  onModalClose
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [professionalSelected, setProfessionalSelected] = useState<Professional | null>(null);

  const toastDef= {
          duration: 3000,
          className: 'toasted',
          style: {
              padding: '16px',
              color: comum.colors.interfaceText400,
          },
          iconTheme: {
              primary: comum.colors.interfaceSuccess,
              secondary: 'var(--cfffaee)',
          },
      }


  async function carregarProfissionais() {
    try {
      const response = await api.get('/usuario/options');
      const profissionais = response.data.result || [];
      setProfessionals(profissionais);
      return profissionais;
    } catch (error) {
      console.error('Erro ao carregar profissionais:', error);
      setProfessionals([]);
      return [];
    }
  }

  useEffect(() => {
    if (isModalOpen) {
      setLoading(true);
      carregarProfissionais().then(() => {
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    if (openModalExternally && !isModalOpen) {
      setIsModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModalExternally]);

  const filteredProfessionals = professionals.filter(professional =>
    professional?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) || false
  );

  const handleProfessionalSelect = (professional: Professional) => {
    setConfirmModalOpen(true);
    setProfessionalSelected(professional);
  };

  const handleClearSelection = () => {
    onSelectProfessional();
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSearchTerm('');
    if (onModalClose) {
      setTimeout(() => {
        onModalClose();
      }, 100);
    }
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const handleConfirmSelection = async () => {

    try {
      const response = await api.put(`/proposta/${id_proposta}/usuario/${professionalSelected?.id_usuario}`);

      if(response.data.status !== 'success') {
        throw new Error(response.data.result[0].message);
      }

      const mensagemSucesso = isAlteracao 
        ? "Profissional alterado com sucesso" 
        : "Profissional selecionado com sucesso";
      toast.success(mensagemSucesso, toastDefs);
      closeConfirmModal();
      closeModal();
      setSearchTerm('');
      setProfessionalSelected(null);
      setTimeout(() => {
        onSelectProfessional(professionalSelected?.id_usuario);
      }, 500);
    } catch (error: any) {
      toast.error("Erro ao selecionar profissional. " + error.message, toastDefsError);
    }
  };

  return (
    <Container className={className}>
      {!openModalExternally && (
        <SelectorButton
          hasSelection={!!selectedProfessional}
          className={error ? 'error' : ''}
          onClick={openModal}
        >
        {selectedProfessional ? (
          <>
            <div className="selected-office">
              <span className="office-name">{selectedProfessional?.nome || 'N/A'}</span>
              <div className="office-codes">
                <span className="code-new">D{selectedProfessional?.id_usuario || ''}</span>
              </div>
            </div>
            <div className="edit-icon">
              <IconEdit color={comum.colors.interfaceText300} />
            </div>
          </>
        ) : (
          <>
            <IconPlus color={comum.colors.interfacePure0} />
            {placeholder}
          </>
        )}
        </SelectorButton>
      )}

      {error && <div className="error-text">{error}</div>}

      {isModalOpen && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{isAlteracao ? "Alterar Profissional" : "Selecionar Profissional"}</h3>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>

            <SearchContainer>
              <input
                type="text"
                placeholder="Buscar por nome ou código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </SearchContainer>

            <OfficeList>
              {loading ? (
                <div className="loading">Carregando profissionais...</div>
              ) : filteredProfessionals.length > 0 ? (
                filteredProfessionals.map((professional) => (
                  <OfficeItem
                    key={professional.id_usuario}
                    isSelected={selectedProfessional?.id_usuario === professional.id_usuario}
                    onClick={() => handleProfessionalSelect(professional)}
                  >
                    <div className="office-info">
                      <div className="office-name">{professional?.nome || 'N/A'}</div>
                      <div className="office-codes">
                        <span className="code-label">Código:</span>
                        <span className="code-new">D{professional?.id_usuario || ''}</span>
                      </div>
                    </div>
                    {selectedProfessional?.id_usuario === professional.id_usuario && (
                      <div className="selected-tag">Selecionado</div>
                    )}
                  </OfficeItem>
                ))
              ) : (
                <NoResultsMessage>
                  Nenhum profissional encontrado para "{searchTerm}"
                </NoResultsMessage>
              )}
            </OfficeList>

                          {selectedProfessional && !isAlteracao && (
                <div className="modal-footer">
                  <ButtonDefault className="cancel" onClick={handleClearSelection}>
                    Limpar Seleção
                  </ButtonDefault>
                </div>
              )}
          </ModalContent>
        </Modal>
      )}

      {confirmModalOpen && (
        <Modal onClick={closeConfirmModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{isAlteracao ? "Alterar Profissional" : "Selecionar Profissional"}</h3>
              <CloseButton onClick={closeConfirmModal}>×</CloseButton>
            </ModalHeader>
            <h3 className="p-6">
              {isAlteracao 
                ? "Deseja realmente alterar o profissional responsável por esta demanda?"
                : "Deseja realmente selecionar este profissional?"}
              <br></br>
              <br></br>
              <span className="text-sm text-gray-500">
                {professionalSelected?.nome}
              </span>
              <br></br>
              <br></br>
              {isAlteracao 
                ? "Todas as pendências em aberto do profissional anterior serão automaticamente finalizadas."
                : "Esta ação não poderá ser desfeita."}
            </h3>
            <div className="modal-footer">
              <ButtonDefault className="cancel" onClick={closeConfirmModal}>
                Cancelar
              </ButtonDefault>

              <ButtonDefault className="continue" onClick={handleConfirmSelection}>
                Confirmar
              </ButtonDefault>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ProfessionalSelector; 